import * as React from 'react';

// lexical
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $createParagraphNode,
  SELECTION_CHANGE_COMMAND,
  LexicalCommand,
  createCommand,
  $createTextNode,
  $isTextNode,
  ElementFormatType,
  $isElementNode,
} from 'lexical';
import {
  $isListNode,
  REMOVE_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
} from '@lexical/list';
import { $isHeadingNode, $createHeadingNode } from '@lexical/rich-text';
import { ListNode } from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  TbArrowBarBoth,
  TbArrowBarLeft,
  TbArrowBarRight,
  TbArticle,
  TbArticleFilledFilled,
  TbBold,
  TbH1,
  TbH2,
  TbItalic,
  TbLink,
  TbList,
  TbListNumbers,
  TbMoodSmile,
  TbAlignLeft,
  TbAlignCenter,
  TbAlignRight,
  TbClearFormatting,
  TbTrash,
  TbArrowsDiagonal,
} from 'react-icons/tb';
import { $isAtNodeEnd } from '@lexical/selection';
import { PopUp } from '../Slate/popup';
import { EmojiPicker } from '../emojis';
import { ActionEntityPosition, ActionStyle } from 'types/domain/action';
import { Color } from '../Slate/toolbar';
import { createPortal } from 'react-dom';
import { FloatingLinkEditor } from './link';
import { CourseVariableSelector } from 'app/pages/Create/Courses/Course/Variables/CourseVariableSelector';

export const LowPriority = 1;
const ADD_EMOJI_COMMAND: LexicalCommand<string> = createCommand();
const ADD_VARIABLE_COMMAND: LexicalCommand<string> = createCommand();

export function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

export interface ToolbarProps extends React.PropsWithChildren {
  onDelete?: (e) => void;
  setColor: (val) => void;
  onExpand?: (val) => void;
  onPosition?: (val) => void;
  onStyle?: (val) => void;
  color: number;
  style?: ActionStyle | number;
  position?: ActionEntityPosition;
  inScene?: boolean;
  canSelectVariable?: boolean;
}

const iconClass = 'w-4 h-4 text-gray-800 m-1';
const selected = 'bg-gray-200';

export const ToolbarPlugin = React.memo(({ ...props }: ToolbarProps) => {
  const toolbarRef = React.useRef(null);
  const [editor] = useLexicalComposerContext();
  const [isLink, setIsLink] = React.useState(false);
  const [isBold, setIsBold] = React.useState(false);
  const [isCode, setIsCode] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [blockType, setBlockType] = React.useState<string>('paragraph');
  const [elementFormat, setElementFormat] = React.useState<ElementFormatType>('center');
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  const updateToolbar = React.useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey: any = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          setBlockType(type);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsCode(selection.hasFormat('code'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(node, parentNode => $isElementNode(parentNode) && !parentNode.isInline());
      }

      setElementFormat(
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
          ? node.getFormatType()
          : parent?.getFormatType() || 'center',
      );
    }
  }, [editor]);

  React.useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);

  React.useEffect(() => {
    return editor.registerCommand(
      ADD_EMOJI_COMMAND,
      payload => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([$createTextNode(payload)]);
        }
        return true;
      },
      1,
    );
  }, [editor]);

  React.useEffect(() => {
    return editor.registerCommand(
      ADD_VARIABLE_COMMAND,
      payload => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([$createTextNode(payload)]);
        }
        return true;
      },
      1,
    );
  }, [editor]);

  const insertLink = React.useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const formatParagraph = React.useCallback(() => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [blockType, editor]);
  const clearNode = React.useCallback(node => {
    if ($isTextNode(node)) {
      node.setFormat(0);
      node.setStyle('');
    }
  }, []);

  const clearFormat = React.useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selection.getNodes().forEach(clearNode);
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [clearNode, editor]);

  const formatHeading = React.useCallback(
    (type: 'h1' | 'h2') => {
      if (blockType !== type) {
        editor.update(() => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode(type));
          }
        });
      } else formatParagraph();
    },
    [blockType, editor, formatParagraph],
  );

  const formatBulletList = React.useCallback(
    (type: 'ul' | 'ol') => {
      if (!['ul', 'ol'].includes(blockType))
        editor.dispatchCommand(type === 'ul' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, '' as any);
      else editor.dispatchCommand(REMOVE_LIST_COMMAND, '' as any);
    },
    [blockType, editor],
  );

  const H1Icon = React.useMemo(() => {
    switch (blockType) {
      case 'h1':
        return <TbH1 className={iconClass} />;
      case 'h2':
        return <TbH2 className={iconClass} />;
      default:
        return <TbH1 className={iconClass} />;
    }
  }, [blockType]);

  const listIcon = React.useMemo(() => {
    switch (blockType) {
      case 'ul':
        return <TbList className={iconClass} />;
      case 'li':
        return <TbListNumbers className={iconClass} />;
      default:
        return <TbList className={iconClass} />;
    }
  }, [blockType]);

  const alignIcon = React.useMemo(() => {
    switch (blockType) {
      case 'ul':
        return <TbAlignLeft className={iconClass} />;
      case 'li':
        return <TbAlignRight className={iconClass} />;
      default:
        return <TbAlignCenter className={iconClass} />;
    }
  }, [blockType]);

  return (
    <div
      className={`${
        props.inScene ? 'absolute -top-16 z-12' : ''
      } mx-auto w-auto inline-flex items-center py-1 px-2 mt-6 rounded-md overflow-visible 
            shadow-lg text-grey-800 bg-white dark:bg-slate-900 cursor-pointer dark:text-white border border-slate-700/40`}
      ref={toolbarRef}
    >
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        aria-label="Format Bold"
        className={isBold ? selected : ''}
      >
        <TbBold className={iconClass} />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        aria-label="Format Italics"
        className={isItalic ? selected : ''}
      >
        <TbItalic className={iconClass} />
      </button>

      <PopUp icon={H1Icon}>
        <button className={blockType === 'h1' ? selected : ''} onClick={e => formatHeading('h1')} aria-label="H1 Text">
          <TbH1 className={iconClass} />
        </button>
        <button className={blockType === 'h2' ? selected : ''} onClick={e => formatHeading('h2')} aria-label="H2 Text">
          <TbH2 className={iconClass} />
        </button>
      </PopUp>
      <PopUp icon={alignIcon}>
        <button
          className={elementFormat === 'left' ? selected : ''}
          onClick={e => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
          aria-label="Align Left"
        >
          <TbAlignLeft className={iconClass} />
        </button>
        <button
          className={elementFormat === 'center' ? selected : ''}
          onClick={e => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
          aria-label="Align Center"
        >
          <TbAlignCenter className={iconClass} />
        </button>
        <button
          className={elementFormat === 'right' ? selected : ''}
          onClick={e => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
          aria-label="Align Right"
        >
          <TbAlignCenter className={iconClass} />
        </button>
      </PopUp>

      <PopUp icon={listIcon}>
        <button
          className={blockType === 'ul' ? selected : ''}
          onClick={e => formatBulletList('ul')}
          aria-label="H1 Text"
        >
          <TbList className={iconClass} />
        </button>
        <button
          className={blockType === 'ol' ? selected : ''}
          onClick={e => formatBulletList('ol')}
          aria-label="H2 Text"
        >
          <TbListNumbers className={iconClass} />
        </button>
      </PopUp>

      {props.onPosition && (
        <PopUp
          icon={
            props.position ? (
              props.position === ActionEntityPosition.LEFT ? (
                <TbArrowBarLeft className={iconClass} />
              ) : props.position === ActionEntityPosition.RIGHT ? (
                <TbArrowBarRight className={iconClass} />
              ) : (
                <TbArrowBarBoth className={iconClass} />
              )
            ) : (
              <TbArrowBarBoth className={iconClass} />
            )
          }
        >
          <button onClick={e => props.onPosition(ActionEntityPosition.LEFT)}>
            <TbArrowBarLeft className={iconClass} />
          </button>
          <button onClick={e => props.onPosition(ActionEntityPosition.CENTER)}>
            <TbArrowBarBoth className={iconClass} />
          </button>
          <button onClick={e => props.onPosition(ActionEntityPosition.RIGHT)}>
            <TbArrowBarRight className={iconClass} />
          </button>
        </PopUp>
      )}
      {props.onExpand && (
        <button onClick={e => props.onExpand(true)}>
          <TbArrowsDiagonal className={iconClass} />
        </button>
      )}
      {props.setColor && <Color color={props.color} setColor={props.setColor} />}
      {props.onStyle && (
        <button onClick={e => props.onStyle(props.style === ActionStyle.BOX ? ActionStyle.LINES : ActionStyle.BOX)}>
          {props.style !== ActionStyle.BOX ? (
            <TbArticleFilledFilled className={iconClass} />
          ) : (
            <TbArticle className={iconClass} />
          )}
        </button>
      )}

      <button
        onClick={e => {
          e.stopPropagation();
          setEmojiOpen(true);
        }}
      >
        <TbMoodSmile className={iconClass} />
      </button>
      <button onClick={insertLink} aria-label="Insert Link">
        <TbLink className={iconClass} />
      </button>
      {/* <button onClick={clearFormat} aria-label="Clear formatting">
        <TbClearFormatting className={iconClass} />
      </button> */}
      {props.onDelete && (
        <button onClick={props.onDelete} aria-label="Delete">
          <TbTrash className={iconClass} />
        </button>
      )}
      {props.canSelectVariable && (
        <CourseVariableSelector
          variableId={null}
          onSelect={variableId => editor.dispatchCommand(ADD_VARIABLE_COMMAND, `[var_${variableId}]`)}
        />
      )}
      <div className="relative">
        <EmojiPicker
          open={emojiOpen}
          setOpen={val => setEmojiOpen(val)}
          select={d => editor.dispatchCommand(ADD_EMOJI_COMMAND, d)}
        />
      </div>
      {isLink && createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
    </div>
  );
});
